import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import { PoolButton, PoolModal, PoolTextArea, PoolTextField } from '@synergy/components';
import { makeStyles } from '@mui/styles';
import { nullIfEmpty } from '@synergy/utils';

import * as TagActions from '../../../../common/actions/tag-actions';
import { ConfigurationTag } from './models/configuration-tag';
import { TagFormikValues, tagFormikValuesFieldNames } from '../models/tag-formik-values';
import { tagsValidationSchema } from './tags-validation-schema';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const useStyles = makeStyles(() => ({
    actionChildren: {
        minWidth: 200,
    },
}));

interface TagsDetailModalProps {
    visible: boolean;
    tag: ConfigurationTag | undefined;
    onClose: () => void;
}

export const TagsDetailModal = (props: TagsDetailModalProps) => {
    const dispatch = useThunkDispatch();
    const classes = useStyles();

    const isNewTag = !props.tag?.id || props.tag.id <= 0;
    const initialValues: TagFormikValues = {
        name: props.tag?.name || '',
        description: props.tag?.description || '',
    };
    const intl = useIntl();

    const handleClose = (formikProps: FormikProps<TagFormikValues>) => {
        formikProps.resetForm();
        props.onClose();
    };

    const handleSubmit = (values: TagFormikValues, formikProps: FormikHelpers<TagFormikValues>) => {
        props.onClose();

        const tag: Partial<ConfigurationTag> = {
            id: props.tag?.id,
            name: values.name,
            description: nullIfEmpty(values.description),
        };

        if (!tag?.id) {
            dispatch(TagActions.createConfigurationTag(tag));
        } else {
            dispatch(TagActions.updateConfigurationTag(tag));
        }

        formikProps.resetForm();
    };

    const actionChildren = (formikProps: FormikProps<TagFormikValues>) => (
        <>
            <PoolButton
                key="1"
                styleType="firstLevel"
                variant="text"
                onClick={() => handleClose(formikProps)}
                className={classes.actionChildren}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton
                key="2"
                styleType="firstLevel"
                onClick={() => formikProps.submitForm()}
                className={classes.actionChildren}
                disabled={Object.keys(formikProps.values.name).length === 0}
            >
                <FormattedMessage id={isNewTag ? 'app.common.add' : 'app.common.save'} />
            </PoolButton>
        </>
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={tagsValidationSchema(intl)}
        >
            {(formikProps: FormikProps<TagFormikValues>) => (
                <Form>
                    <PoolModal
                        title={
                            <FormattedMessage
                                id={isNewTag ? 'app.configuration.createTag' : 'app.configuration.editTag'}
                            />
                        }
                        isVisible={props.visible}
                        handleClose={() => handleClose(formikProps)}
                        actionChildren={actionChildren(formikProps)}
                    >
                        <Grid container={true} spacing={2} sx={{ mt: 1 }}>
                            <Grid item={true} xs={12}>
                                <Field name={tagFormikValuesFieldNames.name}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            {...field}
                                            fullWidth={true}
                                            required={true}
                                            label={<FormattedMessage id="app.configuration.tags.name" />}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                            autoFocus={true}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name={tagFormikValuesFieldNames.description}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextArea
                                            {...field}
                                            fullWidth={true}
                                            rows={3}
                                            label={<FormattedMessage id="app.configuration.tags.description" />}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
